import React, {useState} from 'react';
import Link from 'next/link';
import {
  useTheme,
  IconButton,
  Drawer,
  useMediaQuery,
  Box,
  Button,
  Grid,
  Typography,
  List,
  ListItem,
  ClickAwayListener
} from '@mui/material';
import {usePathname} from 'next/navigation';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

import {AnnouncementBanner} from '../../announcementBanner/AnnouncementBanner/announcementBanner';

import {HeaderProps} from './header.types';
import {useStyles} from './header.styles';

export function Header(props: HeaderProps) {
  const pathname = usePathname();
  const theme = useTheme();
  const classes = useStyles();
  const [showLogin, setShowLogin] = useState<boolean>(false);

  const handleClickAwayLogin = () => {
    setShowLogin(false);
  };

  const userNavClick = () => {
    setShowLogin((login) => !login);
  };

  // Helper function to create conditional class names
  const createClassName = (...classNames: (string | undefined | false)[]): string => {
    return classNames.filter(Boolean).join(' ');
  };

  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {title, announcementBanner, logoutLabel = '', links = [], isLoggedIn = false, loggedInUser = ''} = props;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const logout = () => {
    // Write the logout logic here.
    sessionStorage.removeItem('session');
    sessionStorage.removeItem('loginType');
    sessionStorage.removeItem('domain');
    sessionStorage.removeItem('Session-id');
    window.location.href = `/api/auth/logout`;
  };
  const renderLinks = (isMobileView: boolean) => (
    <nav aria-label="Main Menu" className={classes.navBar}>
      <List data-testid="header-links" className={`${classes.list} ${isMobileView ? 'isMobile' : ''}`}>
        <Box className={`${classes.listItemWrapper} ${isMobileView ? 'isMobile' : ''}`}>
          {links
            .filter((item) => item.isActive)
            .map((item) => {
              return (
                <ListItem
                  className={`${classes.listItem} ${pathname === item.url ? 'active' : ''} ${isMobileView ? 'isMobile' : ''}`}
                  key={item?.url}
                  onClick={() => {
                    if (isMobileView) {
                      handleDrawerToggle();
                    }
                  }}
                >
                  <Link
                    tabIndex={0}
                    className={`${classes.link} ${isMobileView ? 'isMobile' : ''}`}
                    href={item?.url}
                    replace
                  >
                    <Typography
                      variant="body1"
                      color={pathname === item.url ? 'text.onAccent' : 'text.primary'}
                      sx={{fontWeight: '600 !important', transition: 'color 0.3s'}}
                    >
                      {item?.label}
                    </Typography>
                  </Link>
                </ListItem>
              );
            })}
        </Box>
      </List>
    </nav>
  );

  const mobileDrawer = (
    <Drawer
      variant="temporary"
      anchor="left"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      classes={{
        paper: classes.drawerPaper
      }}
      ModalProps={{
        keepMounted: true
      }}
    >
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <IconButton
          onClick={handleDrawerToggle}
          aria-label="close menu"
          title="Close menu"
          data-testid="close-drawer-button"
        >
          <CloseIcon aria-hidden="true" />
        </IconButton>
      </Box>
      {renderLinks(true)}
    </Drawer>
  );

  return (
    <Grid component="header">
      <Grid
        item
        xs={12}
        position="relative"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{padding: '0px 18px', backgroundColor: 'rgba(123, 21, 124, 0.05)'}}
        className={classes.headerContainer}
      >
        <Grid className={classes.optumLogoContainer}>
          <Typography variant="h4" className={classes.homeTitle} tabIndex={0}>
            {title}
          </Typography>
        </Grid>
        <Grid>
          <Grid style={{display: 'flex', alignItems: 'center'}} item>
            {isLoggedIn && (
              <ClickAwayListener onClickAway={handleClickAwayLogin}>
                <Box component="div" className={classes.userNav}>
                  <Button
                    variant="contained"
                    onClick={userNavClick}
                    className={createClassName(classes.userNavBtn, showLogin ? 'active' : '')}
                    sx={{
                      background: 'transparent !important',
                      color: '#3D3C38 !important',
                      boxShadow: 'none !important',
                      border: '0 !important',
                      borderRadius: '0 !important',
                      fontSize: '16px !important',
                      padding: '6px 20px !important',
                      textTransform: 'none !important',
                      '&:hover': {
                        background: 'transparent !important',
                        boxShadow: 'none !important'
                      },
                      fontWeight: 'bold !important'
                    }}
                  >
                    <span className={createClassName('button-label', 'loginUserName')}>{loggedInUser}</span>
                    {showLogin ? <ExpandLess /> : <ExpandMore />}
                  </Button>
                  {showLogin ? (
                    <Box component="div" className={classes.userNavCollapse}>
                      <Button
                        variant="text"
                        className="signOutLabel"
                        tabIndex={0}
                        onClick={logout}
                        sx={{textTransform: 'none !important'}}
                      >
                        {logoutLabel}
                      </Button>
                    </Box>
                  ) : null}
                </Box>
              </ClickAwayListener>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{backgroundColor: '#f5f5f5', padding: '0px 18px'}}
        >
          {links?.length > 0 &&
            (isMobile ? (
              <Box pl={1} display="flex" justifyContent="flex-start" width="100%">
                <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle}>
                  <MenuIcon />
                </IconButton>
              </Box>
            ) : (
              renderLinks(false)
            ))}
        </Grid>
      </Grid>
      {mobileDrawer}
      {announcementBanner?.maintenanceStatus === 'active' ? (
        <AnnouncementBanner
          alertIcon={announcementBanner.alertIcon}
          message={announcementBanner.message}
          altText={announcementBanner.iconAltText}
        />
      ) : (
        ''
      )}
    </Grid>
  );
}

export default Header;
